import React from 'react';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { AlertTriangle, Users, Calendar, DollarSign } from 'lucide-react';
import { Badge } from './badge';

interface CuotaVencida {
  cuotaNumero: number;
  fechaVencimiento: string;
  montoCuota: number;
  diasVencida: number;
}

interface ClienteConCuotasVencidas {
  comprador: string;
  lotes: string[];
  cuotasVencidas: CuotaVencida[];
  totalDeuda: number;
}

interface AlertaCuotasVencidasProps {
  vendedorNombre: string;
  clientesConCuotasVencidas: ClienteConCuotasVencidas[];
  totalCuotasVencidas: number;
  totalMontoVencido: number;
  onDismiss?: () => void;
}

export function AlertaCuotasVencidas({
  vendedorNombre,
  clientesConCuotasVencidas,
  totalCuotasVencidas,
  totalMontoVencido,
  onDismiss
}: AlertaCuotasVencidasProps) {
  const formatGT = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-GT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount: number) => {
    return `Q. ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  const getPrioridadColor = (diasVencida: number) => {
    if (diasVencida > 60) return 'bg-red-500 text-white';
    if (diasVencida > 30) return 'bg-orange-500 text-white';
    return 'bg-yellow-500 text-black';
  };

  return (
    <Alert className="border-orange-200 bg-orange-50 my-4">
      <AlertTriangle className="h-5 w-5 text-orange-600" />
      <div className="flex-1">
        <AlertTitle className="text-orange-800 font-semibold">
          ⚠️ Vendedor con Cuotas Vencidas
        </AlertTitle>
        <AlertDescription className="text-orange-700 mt-2">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span className="font-medium">{vendedorNombre}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{totalCuotasVencidas} cuotas vencidas</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                <span className="font-semibold">{formatCurrency(totalMontoVencido)}</span>
              </div>
            </div>

            {/* Lista de clientes con cuotas vencidas */}
            <div className="bg-white rounded-md border border-orange-200 p-3 max-h-48 overflow-y-auto">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Clientes con cuotas vencidas:
              </div>
              <div className="space-y-2">
                {clientesConCuotasVencidas.map((cliente, idx) => (
                  <div key={idx} className="border-b border-gray-100 last:border-b-0 pb-2 last:pb-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 text-sm">
                          {cliente.comprador}
                        </div>
                        <div className="text-xs text-gray-600 mb-1">
                          Lotes: {cliente.lotes.join(', ')}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {cliente.cuotasVencidas.slice(0, 3).map((cuota, cuotaIdx) => (
                            <Badge 
                              key={cuotaIdx} 
                              className={`text-xs px-1 py-0.5 ${getPrioridadColor(cuota.diasVencida)}`}
                            >
                              Cuota #{cuota.cuotaNumero} ({cuota.diasVencida}d)
                            </Badge>
                          ))}
                          {cliente.cuotasVencidas.length > 3 && (
                            <Badge variant="outline" className="text-xs px-1 py-0.5">
                              +{cliente.cuotasVencidas.length - 3} más
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-red-600">
                          {formatCurrency(cliente.totalDeuda)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {cliente.cuotasVencidas.length} cuota{cliente.cuotasVencidas.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-xs text-orange-600 font-medium">
              💡 Esta alerta es informativa. Puedes continuar con el registro del lote.
            </div>

            {onDismiss && (
              <div className="flex justify-end mt-3">
                <button
                  onClick={onDismiss}
                  className="text-xs text-orange-600 hover:text-orange-800 underline"
                >
                  Ocultar alerta
                </button>
              </div>
            )}
          </div>
        </AlertDescription>
      </div>
    </Alert>
  );
}
